import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Pill,
	PillVariant,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NOVA_MONOSPACE_FONT} from '../shared/novaStudioFonts';
import {NOVA_FILE_ACCEPTED_EXTENSIONS, fileTypeLabel, validateNovaFile} from '../shared/novaFileTypes';
import {NOVA_FILE_READY_STATUS} from '../shared/novaVariableLibrary';

export interface NovaReferenceFile {
	fileId: string;
	filename: string;
	variableToken: string;
	processingStatus: string;
	sizeBytes: number | null;
}

interface Props {
	files: NovaReferenceFile[];
	onAttach: (file: File) => Promise<void>;
	onRemove: (fileId: string) => Promise<void>;
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// The drop target doubles as the Browse trigger's surround. A dashed border reads as "drop here"
// without needing a bespoke DS component; it highlights while a file is dragged over it.
const DropZone = styled.div<{$active: boolean}>`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px 16px;
	border: 1px dashed ${({$active}) => ($active ? NovaStudioColor.focusRing : NovaStudioColor.border)};
	border-radius: 8px;
	background-color: ${({$active}) => ($active ? NovaStudioColor.badgeBackground : 'transparent')};
`;

// The tinted square behind the upload glyph. Icon renders the glyph alone, so the surface behind it
// is ours; it reads the same token the information Pill fills itself with, so the drop zone and the
// file-type badge on each row are the same blue rather than two hand-matched ones.
const UploadBadge = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 8px;
	background-color: ${tokensForThemeProvider.sky.comp.chipsStatus.color.background.information};
`;

// Badge | filename + size | copy-token and Remove together. The actions column sizes to its content
// because the token has to stay readable in full — it is the text you are about to put on the
// clipboard — so the filename column is the one that gives way, truncating with an ellipsis.
const Row = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	column-gap: 12px;
	padding: 12px;
	border: 1px solid ${NovaStudioColor.border};
	border-radius: 8px;
`;

// Never wrapped: the token is a single copyable string, and breaking it across lines would both
// misrepresent it and make the button grow taller than the one beside it.
const Token = styled.span`
	font-family: ${NOVA_MONOSPACE_FONT};
	white-space: nowrap;
`;

const HiddenInput = styled.input`
	display: none;
`;

function formatBytes(bytes: number | null): string {
	if (!bytes || bytes <= 0) {
		return '';
	}
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// The "Files" section on the Prompt tab: drag-drop or browse to attach a document, and a
// list of what is attached. A validated file is usable immediately (stage-and-read), so there is no
// processing spinner, polling or status chip — the row appears once the attach round-trip lands.
//
// Each READY file's {file:...} variable is presented as one copy button rather than a token plus a
// separate action, so the thing you read is the thing you click. Inserting at the cursor lives in the
// variable library above the editors (file variables are merged into its Files scope), which is also
// where the User-prompt-only permission is enforced and explained — so there is no second, weaker
// insertion path to keep in step with it here.
export function NovaReferenceFiles({files, onAttach, onRemove}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const inputRef = useRef<HTMLInputElement>(null);
	const [dragActive, setDragActive] = useState(false);
	const [isBusy, setIsBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// The guard for "one operation at a time" has to be a ref, not the isBusy state: React batches
	// state updates, so two events dispatched before the next render (a drop landing in the same batch
	// as a picker change) would both read isBusy as false and both start. The state exists to drive the
	// UI (button labels and disabled states); this decides whether an operation may begin.
	const inFlightRef = useRef(false);

	const beginOperation = (): boolean => {
		if (inFlightRef.current) {
			return false;
		}
		inFlightRef.current = true;
		setError(null);
		setIsBusy(true);
		return true;
	};

	const endOperation = (): void => {
		inFlightRef.current = false;
		setIsBusy(false);
	};

	// Guard the operation, not just the Browse button: the drop zone and the hidden input stay live
	// during an upload, so without this a second attach starts while the first is in flight — the
	// second's setError(null) wipes the first's failure message, and whichever settles first clears the
	// busy state while the other is still running.
	const attach = (file: File | undefined): void => {
		if (!file || inFlightRef.current) {
			return;
		}
		const validation = validateNovaFile(file);
		if (!validation.ok) {
			setError(
				formatMessage({
					id: validation.reason === 'size' ? 'nova.referenceFiles.errorSize' : 'nova.referenceFiles.errorType',
				})
			);
			return;
		}
		if (!beginOperation()) {
			return;
		}
		onAttach(file)
			.catch(() => setError(formatMessage({id: 'nova.referenceFiles.attachError'})))
			.finally(endOperation);
	};

	const remove = (fileId: string): void => {
		if (!beginOperation()) {
			return;
		}
		onRemove(fileId)
			.catch(() => setError(formatMessage({id: 'nova.referenceFiles.removeError'})))
			.finally(endOperation);
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
		event.preventDefault();
		setDragActive(false);
		attach(event.dataTransfer.files[0]);
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		attach(event.target.files?.[0]);
		// Clear the value so re-selecting the same file still fires onChange.
		event.target.value = '';
	};

	return (
		<Container data-cy="nova-reference-files" data-testid="nova-reference-files">
			<Label>{formatMessage({id: 'nova.referenceFiles.title'})}</Label>
			<DropZone
				$active={dragActive}
				role="group"
				aria-label={formatMessage({id: 'nova.referenceFiles.title'})}
				data-cy="nova-reference-files-dropzone"
				onDragOver={event => {
					event.preventDefault();
					setDragActive(true);
				}}
				onDragLeave={() => setDragActive(false)}
				onDrop={handleDrop}
			>
				<FlexColumn gap="s" alignItems="center">
					<UploadBadge>
						<Icon icon="upload" size="m" color="primary" />
					</UploadBadge>
					{/* While an upload is in flight the whole invitation is replaced by its progress, rather
					    than leaving "Drag a document here, or" standing next to a disabled button: the
					    drop zone is not accepting anything until this finishes, so still inviting a drop
					    would be telling the author something untrue. */}
					{isBusy ? (
						<Text color="auxiliary" data-cy="nova-reference-files-uploading">
							{formatMessage({id: 'nova.referenceFiles.uploading'})}
						</Text>
					) : (
						<FlexRow gap="xs" alignItems="center">
							<Text color="auxiliary">{formatMessage({id: 'nova.referenceFiles.dropHint'})}</Text>
							<Button
								emphasis="low"
								data-cy="nova-reference-files-browse"
								onPress={() => inputRef.current?.click()}
							>
								{formatMessage({id: 'nova.referenceFiles.browse'})}
							</Button>
						</FlexRow>
					)}
				</FlexColumn>
				<HiddenInput
					ref={inputRef}
					type="file"
					accept={NOVA_FILE_ACCEPTED_EXTENSIONS.join(',')}
					data-cy="nova-reference-files-input"
					disabled={isBusy}
					onChange={handleInputChange}
				/>
			</DropZone>
			<Text color="auxiliary">{formatMessage({id: 'nova.referenceFiles.accepted'})}</Text>
			{error && (
				<Text color="error" data-cy="nova-reference-files-error">
					{error}
				</Text>
			)}
			{files.length === 0 ? (
				<Text color="auxiliary" data-cy="nova-reference-files-empty">
					{formatMessage({id: 'nova.referenceFiles.empty'})}
				</Text>
			) : (
				<FlexColumn gap="xs">
					{files.map(file => (
						<Row key={file.fileId} data-cy={`nova-reference-file-${file.fileId}`}>
							<Pill variant={PillVariant.INFORMATION}>{fileTypeLabel(file.filename)}</Pill>
							<FlexColumn gap="xxs" alignItems="flex-start">
								<Text ellipsis>{file.filename}</Text>
								<Text color="auxiliary">{formatBytes(file.sizeBytes)}</Text>
							</FlexColumn>
							<FlexRow gap="xs" alignItems="center">
								{file.processingStatus === NOVA_FILE_READY_STATUS && (
									// The token itself is the button: one target, labelled with exactly the text it
									// puts on the clipboard. Medium emphasis is the design system's outlined
									// ("secondary") button — transparent fill, hairline border, primary-coloured
									// label — which is what makes it read as a chip rather than an action.
									<Button
										emphasis="medium"
										icon="copyLink"
										data-cy={`nova-reference-file-copy-${file.fileId}`}
										title={formatMessage({id: 'nova.referenceFiles.copy'})}
										onPress={() => void navigator.clipboard?.writeText(file.variableToken)}
									>
										<Token>{file.variableToken}</Token>
									</Button>
								)}
								<Button
									// Outlined like the copy button, but monochrome: Remove is a neutral action next
									// to it, not a second primary-coloured one. (MONO has no string alias — only
									// high/medium/low do — so it comes from the enum.)
									emphasis={EMPHASIS.MONO}
									data-cy={`nova-reference-file-remove-${file.fileId}`}
									disabled={isBusy}
									onPress={() => remove(file.fileId)}
								>
									{formatMessage({id: 'nova.referenceFiles.remove'})}
								</Button>
							</FlexRow>
						</Row>
					))}
				</FlexColumn>
			)}
		</Container>
	);
}

export default NovaReferenceFiles;
